import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AlertTriangle, X } from "lucide-react";

import TemplatePageHeader from "./component/TemplatePageHeader";
import BasicInformationCard from "./component/BasicInformationCard";
import TemplateTypeCard from "./component/TemplateTypeCard";
import HeaderCard from "./component/HeaderCard";
import BodyCard from "./component/BodyCard";
import FooterCard from "./component/FooterCard";
import ButtonsCard from "./component/ButtonsCard";
import AuthenticationCard from "./component/AuthenticationCard";
import CarouselCard from "./component/CarouselCard";
import TemplatePreview from "./component/TemplatePreview";
import templateService from "src/services/templateService";
import { showErrorToast } from "src/common/showErrorToast";
import { countVariables } from "./templateUtils";
import { exampleById, formFromExample } from "src/features/Template/templateExamples";

function readSetup() {
  try {
    const parsed = JSON.parse(localStorage.getItem("whatsapp_setup") || "{}");
    return {
      workspaceId: parsed.workspaceId || parsed.workspace_id || null,
      accountId:
        parsed.whatsappAccountId ||
        parsed.whatsapp_account_id ||
        parsed.accountId ||
        parsed.account_id ||
        null,
    };
  } catch {
    return { workspaceId: null, accountId: null };
  }
}

const EMPTY_FORM = {
  name: "",
  language: "en_US",
  category: "MARKETING",
  template_type: "STANDARD",
  message_send_ttl_seconds: "",
  header: {
    format: "NONE",
    text: "",
    example: "",
    media_handle: "",
    media_name: "",
    media_preview: "",
  },
  body: { text: "", examples: [] },
  footer: { text: "" },
  buttons: [],
  authentication: {
    otp_type: "COPY_CODE",
    otp_button_text: "Copy code",
    code_expiration_minutes: 10,
    add_security_recommendation: true,
    package_name: "",
    signature_hash: "",
  },
  carousel: { cards: [] },
};

const NAME_RE = /^[a-z0-9_]+$/;

const TemplatePage = ({ mode = "create" }) => {
  const navigate = useNavigate();
  const { workspaceId, accountId } = useMemo(readSetup, []);

  // AUTO-WA-39: /templates/create?example=<id> starts from a ready-made example.
  const [form, setForm] = useState(() => (mode === "create" && formFromExample(exampleById(new URLSearchParams(window.location.search).get("example")), EMPTY_FORM)) || EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState("");

  const isAuth = form.category === "AUTHENTICATION";
  const isCarousel = form.category === "MARKETING" && form.template_type === "CAROUSEL";

  const bodyVarCount = useMemo(() => countVariables(form.body.text), [form.body.text]);

  // Keep the body sample list the same length as the number of {{n}} tokens.
  useEffect(() => {
    setForm((f) => {
      const examples = [...f.body.examples];
      examples.length = bodyVarCount;
      return { ...f, body: { ...f.body, examples: Array.from(examples, (v) => v || "") } };
    });
  }, [bodyVarCount]);

  const patch = useCallback((next) => setForm((f) => ({ ...f, ...next })), []);
  const patchHeader = useCallback(
    (next) => setForm((f) => ({ ...f, header: { ...f.header, ...next } })),
    [],
  );
  const patchBody = useCallback(
    (next) => setForm((f) => ({ ...f, body: { ...f.body, ...next } })),
    [],
  );
  const patchFooter = useCallback(
    (next) => setForm((f) => ({ ...f, footer: { ...f.footer, ...next } })),
    [],
  );
  const patchAuth = useCallback(
    (next) => setForm((f) => ({ ...f, authentication: { ...f.authentication, ...next } })),
    [],
  );

  const uploadHeaderMedia = useCallback(
    async (file, onDone) => {
      if (!workspaceId || !accountId) return;
      try {
        setUploading(true);
        const res = await templateService.uploadHeaderMedia(workspaceId, accountId, file);
        const handle = res?.data?.data?.handle;
        onDone(handle, file);
      } catch (err) {
        showErrorToast(err, "Could not upload the file.");
        onDone(null, file);
      } finally {
        setUploading(false);
      }
    },
    [workspaceId, accountId],
  );

  const buildPayload = () => {
    const p = {
      name: form.name.trim(),
      language: form.language,
      category: form.category,
    };

    if (form.message_send_ttl_seconds) {
      p.message_send_ttl_seconds = Number(form.message_send_ttl_seconds);
    }

    if (isAuth) {
      const a = form.authentication;
      p.authentication = {
        otp_type: a.otp_type,
        otp_button_text: a.otp_button_text || undefined,
        code_expiration_minutes: a.code_expiration_minutes || undefined,
        add_security_recommendation: !!a.add_security_recommendation,
      };
      if (a.otp_type !== "COPY_CODE") {
        p.authentication.package_name = a.package_name || undefined;
        p.authentication.signature_hash = a.signature_hash || undefined;
      }
      return p;
    }

    p.template_type = isCarousel ? "CAROUSEL" : "STANDARD";

    if (form.header.format !== "NONE" && !isCarousel) {
      p.header = { format: form.header.format };
      if (form.header.format === "TEXT") {
        p.header.text = form.header.text;
        if (countVariables(form.header.text) > 0) p.header.example = form.header.example;
      }
      if (["IMAGE", "VIDEO", "DOCUMENT"].includes(form.header.format)) {
        p.header.media_handle = form.header.media_handle;
      }
    }

    p.body = { text: form.body.text, examples: form.body.examples };

    if (!isCarousel && form.footer.text.trim()) {
      p.footer = { text: form.footer.text.trim() };
    }

    if (!isCarousel && form.buttons.length) {
      p.buttons = form.buttons.map(({ id, ...b }) => b);
    }

    if (isCarousel) {
      p.carousel = {
        cards: form.carousel.cards.map((card) => ({
          header: { format: card.header.format, media_handle: card.header.media_handle },
          body: { text: card.body.text, examples: card.body.examples },
          buttons: (card.buttons || []).map(({ id, ...b }) => b),
        })),
      };
    }

    return p;
  };

  const validate = () => {
    if (!form.name.trim()) return "Give the template a name.";
    if (!NAME_RE.test(form.name.trim()))
      return "The name can only use lowercase letters, numbers and underscores.";
    if (!form.language) return "Choose a language.";

    if (isAuth) {
      if (form.authentication.otp_type !== "COPY_CODE") {
        if (!form.authentication.package_name.trim() || !form.authentication.signature_hash.trim())
          return "One-tap and zero-tap codes need the Android package name and signature hash.";
      }
      return "";
    }

    if (!form.body.text.trim()) return "The message body can’t be empty.";
    if (isCarousel && form.body.text.length > 160)
      return "For carousels the intro message must be 160 characters or fewer.";
    if (bodyVarCount > 0 && form.body.examples.some((v) => !v.trim()))
      return "Add a sample value for every variable in the body.";

    if (form.header.format === "TEXT" && !form.header.text.trim())
      return "Add the header text or set the header to None.";
    if (
      ["IMAGE", "VIDEO", "DOCUMENT"].includes(form.header.format) &&
      !form.header.media_handle &&
      !isCarousel
    )
      return "Upload a sample file for the media header.";

    if (isCarousel) {
      if (!form.carousel.cards.length) return "Add at least one carousel card.";
      for (const [i, card] of form.carousel.cards.entries()) {
        if (!card.header.media_handle) return `Upload the media for card ${i + 1}.`;
        if (!card.body.text.trim()) return `Add the body text for card ${i + 1}.`;
      }
    }

    const urls = form.buttons.filter((b) => b.type === "URL").length;
    const phones = form.buttons.filter((b) => b.type === "PHONE_NUMBER").length;
    if (urls > 2) return "A template can have at most 2 URL buttons.";
    if (phones > 1) return "A template can have at most 1 call button.";
    for (const b of form.buttons) {
      if (b.type !== "COPY_CODE" && !b.text.trim()) return "Every button needs a label.";
      if (b.type === "URL" && !b.url.trim()) return "Add the website address for the URL button.";
      if (b.type === "PHONE_NUMBER" && !b.phone_number.trim())
        return "Add the phone number for the call button.";
    }

    return "";
  };

  const handleSubmit = async () => {
    setFormError("");
    const problem = validate();
    if (problem) {
      setFormError(problem);
      return;
    }
    if (!workspaceId || !accountId) {
      setFormError("Connect a WhatsApp account before creating templates.");
      return;
    }

    try {
      setSubmitting(true);
      await templateService.create(workspaceId, accountId, buildPayload());
      toast.success("Template submitted to Meta for review.");
      navigate("/templates");
    } catch (err) {
      const data = err?.response?.data;
      const firstFieldError = data?.errors && Object.values(data.errors)[0]?.[0];
      setFormError(data?.message || firstFieldError || "Could not create the template.");
    } finally {
      setSubmitting(false);
    }
  };

  if (mode !== "create") {
    return (
      <div className="wa-legacy wa-tpl-editor flex h-full flex-col">
        <TemplatePageHeader mode={mode} onBack={() => navigate("/templates")} />
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <p className="max-w-sm text-sm text-slate-500">
            Editing an existing template isn’t available yet. Approved templates
            are managed in WhatsApp Manager; use the preview from the Templates
            list to review one.
          </p>
        </div>
      </div>
    );
  }

  if (!workspaceId || !accountId) {
    return (
      <div className="wa-legacy wa-tpl-editor flex h-full flex-col">
        <TemplatePageHeader mode="create" onBack={() => navigate("/templates")} />
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <p className="max-w-sm text-sm text-slate-500">
            Connect a WhatsApp account in Settings before creating a template.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="wa-legacy wa-tpl-editor flex h-full flex-col">
      <TemplatePageHeader
        mode="create"
        loading={submitting}
        onBack={() => navigate("/templates")}
        onSave={handleSubmit}
      />

      <div className="flex-1 overflow-hidden">
        <div className="mx-auto flex h-[calc(100vh-81px)] max-w-[1500px] gap-8 overflow-hidden px-6 py-6 xl:px-10">
          {/* Form column */}
          <div className="flex-1 overflow-y-auto pb-16 pr-1">
            <div className="mx-auto max-w-2xl space-y-6">
              {formError && (
                <div className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
                  <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                  <span className="flex-1">{formError}</span>
                  <button
                    onClick={() => setFormError("")}
                    className="rounded p-0.5 text-red-400 hover:bg-red-100 hover:text-red-600"
                  >
                    <X size={15} />
                  </button>
                </div>
              )}

              <Section step={1} title="Basics">
                <BasicInformationCard value={form} onChange={patch} />
              </Section>

              {form.category === "MARKETING" && (
                <Section step={2} title="Template type">
                  <TemplateTypeCard value={form} onChange={patch} />
                </Section>
              )}

              {isAuth ? (
                <Section step={2} title="Authentication">
                  <AuthenticationCard value={form.authentication} onChange={patchAuth} />
                </Section>
              ) : isCarousel ? (
                <>
                  <Section step={3} title="Intro message">
                    <BodyCard
                      body={form.body}
                      variableCount={bodyVarCount}
                      onChange={patchBody}
                      maxLength={160}
                      label="Intro message"
                      hint="Shown above the carousel. Max 160 characters."
                    />
                  </Section>
                  <Section step={4} title="Cards">
                    <CarouselCard
                      carousel={form.carousel}
                      onChange={(carousel) => patch({ carousel })}
                      onUpload={uploadHeaderMedia}
                      uploading={uploading}
                    />
                  </Section>
                </>
              ) : (
                <>
                  <Section step={form.category === "MARKETING" ? 3 : 2} title="Header">
                    <HeaderCard
                      header={form.header}
                      onChange={patchHeader}
                      onUpload={uploadHeaderMedia}
                      uploading={uploading}
                    />
                  </Section>
                  <Section step={form.category === "MARKETING" ? 4 : 3} title="Body">
                    <BodyCard
                      body={form.body}
                      variableCount={bodyVarCount}
                      onChange={patchBody}
                      maxLength={1024}
                    />
                  </Section>
                  <Section step={form.category === "MARKETING" ? 5 : 4} title="Footer">
                    <FooterCard footer={form.footer} onChange={patchFooter} />
                  </Section>
                  <Section step={form.category === "MARKETING" ? 6 : 5} title="Buttons">
                    <ButtonsCard
                      buttons={form.buttons}
                      onChange={(buttons) => patch({ buttons })}
                    />
                  </Section>
                </>
              )}
            </div>
          </div>

          {/* Preview column */}
          <div className="hidden w-[380px] flex-shrink-0 xl:block">
            <div className="sticky top-0">
              <TemplatePreview form={form} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function Section({ step, title, children }) {
  return (
    <section>
      <div className="mb-2.5 flex items-center gap-2.5">
        <span className="wa-step-num" aria-hidden="true">{step}</span>
        <h2 className="ws-h3" style={{ color: "#102F45" }}>{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default TemplatePage;
